import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { Context } from '@atlassian/post-office-context';
import {
	type ErrorBoundariedProps,
	type MessageProps,
	createPlacementComponent,
	useDisableChoreographer,
	useInitialData,
} from '@post-office/placement-common';
import { type ComponentType, useEffect, useState } from 'react';

import { ConfluenceHomeBannerPlacementSkeleton } from './skeleton';
import { MessageRenderer, type renderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { withConfluenceHomeBannerIntlProvider } from '../__generated__/intl-provider';
import { type ConfluenceHomeBannerInitialData } from '../api/types';
import { type ConfluenceHomeBannerPlacement } from '../contracts/Placement';

export type Message = MessageProps<typeof renderer>;

type ConfluenceHomeBannerProps = {
	initialData: Promise<Response>;
	context: Context;
} & ConfluenceHomeBannerPlacement;

// https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/gates/post_office_conf_banner_shadow_traffic_in_conf
const CONFLUENCE_HOME_BANNER_SHADOW_TRAFFIC_GATE = 'post_office_conf_banner_shadow_traffic_in_conf';

const InvisibleConfluenceHomeBannerComponent = createPlacementComponent(
	PLACEMENT_ID,
	({ initialData }: ConfluenceHomeBannerProps) => {
		useInitialData(initialData);
		return null;
	},
);

export const ConfluenceHomeBannerPlacementComponent: ComponentType<
	ErrorBoundariedProps<ConfluenceHomeBannerPlacement>
> = withConfluenceHomeBannerIntlProvider(
	createPlacementComponent<ConfluenceHomeBannerPlacement>(PLACEMENT_ID, ({ initialData }) => {
		// Disabling this placement from being choreographed as it is not a pop-up
		useDisableChoreographer();

		const { data, isLoading } = useInitialData<ConfluenceHomeBannerInitialData>(initialData);
		const [messageState, setMessageState] = useState<{
			messages: Message[];
			status: 'loading' | 'done';
		}>({
			messages: [],
			status: 'loading',
		});

		useEffect(() => {
			if (!isLoading) {
				setMessageState({
					messages: (data?.messages || []) as Message[],
					status: 'done',
				});
			}
		}, [data, isLoading]);

		if (isLoading || messageState.status === 'loading') {
			return <ConfluenceHomeBannerPlacementSkeleton />;
		}

		return (
			<>
				{messageState.messages
					.filter(
						(message) =>
							message.messageTemplateId !== 'test-message-inapp' &&
							message.messageTemplateId !== 'message-template-template',
					)
					.slice(0, 1)
					.map(({ messageInstanceId, ...props }) => (
						// Adding @ts-ingore to suppress the error in confluence typecheck and below error is not thrown in post-office typecheck
						// This error exists because confluence is using react 16 and post-office is using react 18
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore - 'ReactNode | ReactElement<any, any>' is not a valid JSX element.
						<MessageRenderer
							{...props}
							key={messageInstanceId}
							id={messageInstanceId}
							messageInstanceId={messageInstanceId}
							onDismissed={() =>
								setMessageState({
									messages: [],
									status: 'done',
								})
							}
						/>
					))}
			</>
		);
	}),
);

const ConfluenceHomeBanner = (props: ConfluenceHomeBannerProps) => {
	const isShadowTrafficEnabled = FeatureGates.checkGate(CONFLUENCE_HOME_BANNER_SHADOW_TRAFFIC_GATE);

	return isShadowTrafficEnabled ? (
		<InvisibleConfluenceHomeBannerComponent {...props} />
	) : (
		<ConfluenceHomeBannerPlacementComponent {...props} />
	);
};

export default ConfluenceHomeBanner;
